import { useAtomValue } from "jotai";
import type { JSX } from "react";

import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Textarea } from "@frontend/components/atoms/Textarea";
import { useField } from "@frontend/hooks/form";
import { ClientField } from "@frontend/shared/client-field";
import { EmployeeField } from "@frontend/shared/employee-field";
import { ReferenceField } from "@frontend/shared/reference-field";
import { useLingui } from "@lingui/react/macro";

import { clientAtom, clientForm, optionsAtom } from "../stores/editor";
import { Section } from "./Section";

const fields = clientForm.fields;

export function ProvenanceSection(): JSX.Element {
  const { t } = useLingui();

  const options = useAtomValue(optionsAtom);
  const client = useAtomValue(clientAtom);

  const provenanceId = useField(fields.provenanceId);
  const note = useField(fields.provenanceNote);

  const picked = options.provenances.find(
    ({ id }) => id === provenanceId.value,
  );

  const wantsNote = true === picked?.needsNote;
  const wantsClient = true === picked?.needsClient;
  const wantsEmployee = true === picked?.needsEmployee;

  return (
    <Section icon="Compass" legend={t`Provenance`}>
      <ReferenceField
        label={t`Brought in by`}
        placeholder={t`Please choose an option`}
        items={options.provenances}
        field={fields.provenanceId}
      />

      {wantsNote ? (
        <Field orientation="vertical">
          <FieldLabel>{t`Detail`}</FieldLabel>

          <FieldContent>
            <Textarea
              rows={2}
              value={note.value ?? ""}
              onChange={(event) => note.setValue(event.target.value)}
            />
          </FieldContent>
        </Field>
      ) : null}

      {wantsClient ? (
        <ClientField
          field={fields.provenanceClientId}
          fallbackLabel={client?.provenanceClientLabel}
        />
      ) : null}

      {wantsEmployee ? (
        <EmployeeField
          field={fields.provenanceEmployeeId}
          fallbackLabel={client?.provenanceEmployeeLabel}
        />
      ) : null}
    </Section>
  );
}
